/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Fragment, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, css } from 'styled-components';
import { isLivestream } from '../../../../../play-core/models/Media';
import { Viewports } from '../../../../../play-core/styles/Viewports';
import { useAnalyticsDispatch } from '../../../analytics/AnalyticsContext';
import { useUdpSectionTracking } from '../../../analytics/use-udp-section-tracking';
import { useHydrated } from '../../../hooks/use-hydrated';
import { usePathByMediaType } from '../../../hooks/use-path-by-media-type';
import { getMediaAria } from '../../../utils/aria-helper';
import { SmartLink } from '../../ClickableComponents/SmartLink';
import { teaserImageWidth } from '../../Image/image-width';
import { PlayImage } from '../../Image/PlayImage';
import { useSectionMeta } from '../../Section/SectionMetaProvider';
import { MediaImageOverlay } from '../TeaserActions/MediaImageOverlay';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const MediaTeaserWrapper = /*#__PURE__*/memo(({
  children,
  media,
  index,
  linkStyles,
  ariaLabel,
  horizontalOnMobile = false,
  horizontalContainerStyles,
  hasBorderRadius,
  isHoveringParent = false,
  isFocusedParent = false,
  showWatchLaterAction = !isLivestream(media),
  imageWidth = teaserImageWidth,
  smallImageWidth = teaserImageWidth,
  mediumImageWidth = teaserImageWidth,
  largeImageWidth = teaserImageWidth,
  xLargeImageWidth = teaserImageWidth,
  ...rest
}) => {
  const {
    t
  } = useTranslation();
  const analytics = useAnalyticsDispatch();
  const [isHovering, setHover] = useState(false);
  const [isFocused, setFocus] = useState(false);
  const sectionMeta = useSectionMeta();
  const {
    sectionId,
    representationName,
    sectionTitle
  } = sectionMeta;
  const trackSection = useUdpSectionTracking();
  const isHydrated = useHydrated();
  return /*#__PURE__*/_jsxs(Layout, {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    onFocus: () => setFocus(true),
    onBlur: () => setFocus(false),
    children: [/*#__PURE__*/_jsxs(StyledLink, {
      to: usePathByMediaType(media),
      "aria-label": isHydrated ? ariaLabel || getMediaAria(media, t) : undefined,
      horizontalOnMobile: horizontalOnMobile,
      linkStyles: linkStyles,
      isInternal: true,
      state: {
        media
      },
      onClick: () => {
        if (sectionId || representationName) {
          analytics({
            type: 'click-event',
            name: 'MediaTeaserClick',
            value1: media.urn,
            value2: sectionId,
            value3: sectionTitle,
            value4: representationName
          });
          trackSection({
            sectionMeta,
            item: {
              ...media,
              index
            }
          });
        }
      },
      children: [/*#__PURE__*/_jsx(PlayImage, {
        src: media.imageUrl,
        alt: media.title,
        width: imageWidth,
        smallImageWidth: smallImageWidth,
        mediumImageWidth: mediumImageWidth,
        largeImageWidth: largeImageWidth,
        xLargeImageWidth: xLargeImageWidth,
        aspectRatio: '16/9',
        hasBorderRadius: hasBorderRadius
      }), horizontalOnMobile ? /*#__PURE__*/_jsx(ContentLayout, {
        styles: horizontalContainerStyles,
        children: children
      }) : /*#__PURE__*/_jsx(Fragment, {
        children: children
      })]
    }), /*#__PURE__*/_jsx(MediaImageOverlay, {
      aspectRatio: 16 / 9,
      isHovering: isHovering || isHoveringParent,
      isFocused: isFocused || isFocusedParent,
      media: media,
      horizontalOnMobile: horizontalOnMobile,
      showWatchLaterAction: showWatchLaterAction,
      ...rest
    })]
  });
});
const Layout = styled.section.withConfig({
  displayName: "MediaTeaserWrapper__Layout",
  componentId: "sc-5tzc7y-0"
})(["position:relative;"]);
const horizontalOnMobileStyles = css(["", "{display:grid;grid-template-columns:minmax(0,1fr) 60%;grid-column-gap:8px;}"], Viewports.for('small'));
const StyledLink = styled(({
  linkStyles,
  horizontalOnMobile,
  ...linkProps
}) => /*#__PURE__*/_jsx(SmartLink, {
  ...linkProps
})).withConfig({
  shouldForwardProp: () => true
}).withConfig({
  displayName: "MediaTeaserWrapper__StyledLink",
  componentId: "sc-5tzc7y-1"
})(["display:flex;flex-direction:column;position:relative;cursor:pointer;", ";", ";"], ({
  horizontalOnMobile
}) => horizontalOnMobile && horizontalOnMobileStyles, ({
  linkStyles
}) => linkStyles);
export const ContentLayout = styled.div.withConfig({
  displayName: "MediaTeaserWrapper__ContentLayout",
  componentId: "sc-5tzc7y-2"
})(["", ";"], ({
  styles
}) => styles);